/**
 * Dev-only agent-endpoint override.
 *
 * Lets a developer running the front under `vite dev` point agent calls (the
 * change control specialist, etc.) at a LOCAL agents server (`localhost:<port>`)
 * instead of the deployed Cloud Run service resolved from `system/config`.
 *
 * Gating: every function short-circuits unless `import.meta.env.DEV` — which is
 * true ONLY under the vite dev server. `yarn build-dev|staging|prod` all build
 * with `PROD=true`, so the override (and the switch UI) is dead code in every
 * deployed build, including deployed dev.
 */

export interface AgentEndpointOverride {
  mode: 'cloud' | 'local'
  port: number
}

const STORAGE_KEY = 'mac.dev.agentEndpointOverride'
const DEFAULT_PORT = 8000

/** Whether the switch may exist at all (vite dev server only). */
export function isAgentEndpointSwitchAvailable(): boolean {
  return import.meta.env.DEV
}

export function getAgentEndpointOverride(): AgentEndpointOverride {
  if (!import.meta.env.DEV) return { mode: 'cloud', port: DEFAULT_PORT }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { mode: 'cloud', port: DEFAULT_PORT }
    const parsed = JSON.parse(raw) as Partial<AgentEndpointOverride>
    return {
      mode: parsed.mode === 'local' ? 'local' : 'cloud',
      port: Number.isInteger(parsed.port) && (parsed.port as number) > 0 ? (parsed.port as number) : DEFAULT_PORT,
    }
  } catch {
    return { mode: 'cloud', port: DEFAULT_PORT }
  }
}

export function setAgentEndpointOverride(override: AgentEndpointOverride): void {
  if (!import.meta.env.DEV) return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(override))
}

/**
 * Resolve the base URL for agent calls: the configured cloud URL, unless the
 * dev-only override selects the local agents server.
 */
export function resolveAgentBaseUrl(cloudUrl: string): string {
  if (import.meta.env.DEV) {
    const o = getAgentEndpointOverride()
    // The DEFAULT local port goes through the vite proxy (`/local-agents` →
    // 127.0.0.1:8001, see vite.config server.proxy): same-origin, so the page
    // works from ANY host that reaches vite — tunnel, Tailscale, IP — without
    // the browser needing a route to the VPS's loopback. A NON-default port is
    // a developer running their own server on their own machine: keep the
    // direct localhost URL for that workflow.
    if (o.mode === 'local') {
      return isRemotePage() || o.port === LOCAL_AGENTS_PORT
        ? '/local-agents'
        : `http://localhost:${o.port}`
    }
  }
  return cloudUrl
}

/**
 * Resolve the base URL of the DEV local command runner, or `null` in cloud mode.
 *
 * Since MYAC-930 a mutation is a command doc, and Eventarc — a property of the
 * cloud project, not of the client — routes it to the deployed assessments
 * service. So "run this one locally" is not a URL change on an existing call:
 * the browser has to deposit the intent somewhere else entirely. That somewhere
 * is `src/dev/local_runner.py` in the assessments repo, which admits the command
 * and writes the doc ALREADY CONCLUDED — which is exactly what makes the cloud
 * handler stand down when Eventarc delivers the create.
 *
 * The runner is paired with the agents server it wakes, so its port is DERIVED
 * from the switch's port rather than configured separately: one number to keep
 * straight instead of two that can silently disagree (8001 → 8101).
 */
const RUNNER_PORT_OFFSET = 100

/** The one agents port the vite proxy targets (8001; runner derived: 8101). */
const LOCAL_AGENTS_PORT = 8001

/** Served from anywhere but localhost ⇒ a `http://localhost:<port>` URL points
 * at the VIEWER'S machine, never at the dev loop — so remote pages always ride
 * the vite proxy, whatever port the (per-origin!) stored override carries. */
function isRemotePage(): boolean {
  return (
    typeof window !== 'undefined' &&
    !['localhost', '127.0.0.1'].includes(window.location.hostname)
  )
}

/** The runner's port, derived from the agents port it is paired with (8001 → 8101). */
export function runnerPortFor(agentPort: number): number {
  return agentPort + RUNNER_PORT_OFFSET
}

export function resolveLocalRunnerUrl(): string | null {
  if (!import.meta.env.DEV) return null
  const o = getAgentEndpointOverride()
  if (o.mode !== 'local') return null
  // Same split as `resolveAgentBaseUrl`: remote pages and the default port
  // ride the vite proxy; a custom port on a localhost page stays direct.
  return isRemotePage() || o.port === LOCAL_AGENTS_PORT
    ? '/local-runner'
    : `http://localhost:${runnerPortFor(o.port)}`
}

/**
 * Dev-only CHATBOT overrides — same gating story as above (dead code in every
 * deployed build), but launch-time via env instead of a runtime switch: the
 * chatbot is a separate ADK server from the agents one, and pointing the chat
 * at a local instance is a per-session decision made when starting `vite dev`.
 *
 *   VITE_CHATBOT_URL_OVERRIDE=http://localhost:8010  — target a local chatbot
 *     server instead of the `system/config.chatbot_url` Cloud Run URL.
 *   VITE_CHATBOT_APP_OVERRIDE=eudamed_expert_agent   — chat with a specific
 *     (e.g. draft) ADK app instead of the configured `chatbot_name`.
 */
export function getChatbotUrlOverride(): string | null {
  if (!import.meta.env.DEV) return null
  return (import.meta.env.VITE_CHATBOT_URL_OVERRIDE as string | undefined) || null
}

export function getChatbotAppOverride(): string | null {
  if (!import.meta.env.DEV) return null
  return (import.meta.env.VITE_CHATBOT_APP_OVERRIDE as string | undefined) || null
}

/**
 * Local rail only (MYAC-1398, same gating as the two overrides above — dead code
 * in every deployed build). Since MYAC-1519 a PROJECT chat is a `send_message`
 * command that the DEPLOYED chatbot admits through Eventarc, so a local chatbot
 * named by VITE_CHATBOT_URL_OVERRIDE never sees a project turn — the deployed
 * service answers with whatever branch it is on. With this flag the front keeps
 * the pre-1519 direct path (`createSession` + `/run_sse`) for project chats too,
 * against the overridden URL, so a branch can be exercised end to end locally.
 *
 *   VITE_CHATBOT_URL_OVERRIDE=http://localhost:8012 VITE_CHATBOT_DIRECT_TURNS=1
 */
export function chatbotDirectTurns(): boolean {
  if (!import.meta.env.DEV) return false
  return !!getChatbotUrlOverride() && import.meta.env.VITE_CHATBOT_DIRECT_TURNS === '1'
}
